import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Plus, Trash2, Edit } from "lucide-react";
import { toast } from "sonner";

export default function Preisliste() {
  const [products, setProducts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showHinweisModal, setShowHinweisModal] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    kunde: "",
    tp: "",
    ba: "",
    five_plus_one: "",
    ap: "",
    punkte: "",
    ust_percent: "19"
  });
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    loadProducts();
    checkHinweisStatus();
  }, []);

  const checkHinweisStatus = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Zeige Modal nur wenn Hinweis noch nicht gelesen
      if (!response.data.preisliste_hinweis_gelesen) {
        setShowHinweisModal(true);
      }
    } catch (error) {
      console.error("Fehler beim Laden des Hinweis-Status:", error);
    }
  };

  const handleHinweisVerstanden = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/auth/mark-preisliste-hinweis`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowHinweisModal(false);
    } catch (error) {
      console.error("Fehler beim Speichern des Hinweis-Status:", error);
      // Modal trotzdem schließen
      setShowHinweisModal(false);
    }
  };

  const loadProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      toast.error("Fehler beim Laden der Produkte");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        ...formData,
        kunde: parseFloat(formData.kunde || 0),
        tp: parseFloat(formData.tp || 0),
        ba: parseFloat(formData.ba || 0),
        five_plus_one: parseFloat(formData.five_plus_one || 0),
        ap: parseFloat(formData.ap || 0),
        punkte: parseFloat(formData.punkte || 0),
        ust_percent: parseFloat(formData.ust_percent)
      };

      if (editingId) {
        await axios.put(`${API}/products/${editingId}`, payload);
        toast.success("Produkt aktualisiert");
      } else {
        await axios.post(`${API}/products`, payload);
        toast.success("Produkt hinzugefügt");
      }
      
      setFormData({
        name: "",
        kunde: "",
        tp: "",
        ba: "",
        five_plus_one: "",
        ap: "",
        punkte: "",
        ust_percent: "19"
      });
      setEditingId(null);
      setShowForm(false);
      loadProducts();
    } catch (error) {
      toast.error("Fehler beim Speichern");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Produkt wirklich löschen?")) return;
    
    try {
      await axios.delete(`${API}/products/${id}`);
      toast.success("Produkt gelöscht");
      loadProducts();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const handleEdit = (product) => {
    setFormData({
      name: product.name,
      kunde: product.kunde.toString(),
      tp: product.tp.toString(),
      ba: product.ba.toString(),
      five_plus_one: product.five_plus_one.toString(),
      ap: product.ap.toString(),
      punkte: product.punkte.toString(),
      ust_percent: product.ust_percent.toString()
    });
    setEditingId(product.id);
    setShowForm(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <div className="max-w-7xl mx-auto px-6 py-8">
        <Link to="/dashboard">
          <Button variant="outline" className="mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Zurück zu Inventory
          </Button>
        </Link>

        <div className="bg-white rounded-2xl shadow-lg p-8 border border-slate-200">
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-slate-800">Preisliste</h1>
            <p className="text-slate-500 mt-1">Alle Produkte mit Preiskategorien</p>
            <Button 
              onClick={() => {
                setShowForm(!showForm);
                setEditingId(null);
                setFormData({
                  name: "",
                  vk_price: "",
                  kunde: "",
                  tp: "",
                  five_plus_one: "",
                  ba: "",
                  ap: "",
                  punkte: "",
                  ust_percent: "19"
                });
              }}
              className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 mt-4"
            >
              <Plus className="w-4 h-4 mr-2" />
              Produkt hinzufügen
            </Button>
          </div>

          {showForm && (
            <form onSubmit={handleSubmit} className="mb-8 p-6 bg-slate-50 rounded-xl border border-slate-200">
              <h3 className="text-lg font-semibold mb-4">
                {editingId ? "Produkt bearbeiten" : "Neues Produkt"}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
                <div className="md:col-span-2">
                  <Label htmlFor="name">Produktname *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                    placeholder="z.B. Optimalset"
                  />
                </div>
                <div>
                  <Label htmlFor="ust_percent">USt. % *</Label>
                  <Input
                    id="ust_percent"
                    type="number"
                    step="0.01"
                    value={formData.ust_percent}
                    onChange={(e) => setFormData({...formData, ust_percent: e.target.value})}
                    required
                    placeholder="19"
                  />
                </div>
                <div>
                  <Label htmlFor="punkte">Punkte</Label>
                  <Input
                    id="punkte"
                    type="number"
                    step="0.01"
                    value={formData.punkte}
                    onChange={(e) => setFormData({...formData, punkte: e.target.value})}
                    placeholder="0"
                  />
                </div>
                <div>
                  <Label htmlFor="kunde">Kunde (€) *</Label>
                  <Input
                    id="kunde"
                    type="number"
                    step="0.01"
                    value={formData.kunde}
                    onChange={(e) => setFormData({...formData, kunde: e.target.value})}
                    required
                    placeholder="111.9"
                  />
                </div>
                <div>
                  <Label htmlFor="tp">TP (€)</Label>
                  <Input
                    id="tp"
                    type="number"
                    step="0.01"
                    value={formData.tp}
                    onChange={(e) => setFormData({...formData, tp: e.target.value})}
                    placeholder="31.5"
                  />
                </div>
                <div>
                  <Label htmlFor="ba">BA (€)</Label>
                  <Input
                    id="ba"
                    type="number"
                    step="0.01"
                    value={formData.ba}
                    onChange={(e) => setFormData({...formData, ba: e.target.value})}
                    placeholder="10"
                  />
                </div>
                <div>
                  <Label htmlFor="five_plus_one">5+1 (€)</Label>
                  <Input
                    id="five_plus_one"
                    type="number"
                    step="0.01"
                    value={formData.five_plus_one}
                    onChange={(e) => setFormData({...formData, five_plus_one: e.target.value})}
                    placeholder="0"
                  />
                </div>
                <div>
                  <Label htmlFor="ap">AP (€)</Label>
                  <Input
                    id="ap"
                    type="number"
                    step="0.01"
                    value={formData.ap}
                    onChange={(e) => setFormData({...formData, ap: e.target.value})}
                    placeholder="50"
                  />
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700">
                  {editingId ? "Aktualisieren" : "Hinzufügen"}
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => {
                    setShowForm(false);
                    setEditingId(null);
                  }}
                >
                  Abbrechen
                </Button>
              </div>
            </form>
          )}

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-slate-200">
                  <th className="text-left py-3 px-2 font-semibold text-slate-700">Produkt</th>
                  <th className="text-right py-3 px-2 font-semibold text-slate-700">Kunde</th>
                  <th className="text-right py-3 px-2 font-semibold text-slate-700">TP</th>
                  <th className="text-right py-3 px-2 font-semibold text-slate-700">BA</th>
                  <th className="text-right py-3 px-2 font-semibold text-slate-700">5+1</th>
                  <th className="text-right py-3 px-2 font-semibold text-slate-700">AP</th>
                  <th className="text-right py-3 px-2 font-semibold text-slate-700">Punkte</th>
                  <th className="text-right py-3 px-2 font-semibold text-slate-700">USt.%</th>
                  <th className="text-right py-3 px-2 font-semibold text-slate-700">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {products.map((product) => (
                  <tr key={product.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-3 px-2 font-medium">{product.name}</td>
                    <td className="py-3 px-2 text-right">{product.kunde > 0 ? `${product.kunde.toFixed(2)} €` : '-'}</td>
                    <td className="py-3 px-2 text-right">{product.tp > 0 ? `${product.tp.toFixed(2)} €` : '-'}</td>
                    <td className="py-3 px-2 text-right">{product.ba > 0 ? `${product.ba.toFixed(2)} €` : '-'}</td>
                    <td className="py-3 px-2 text-right">{product.five_plus_one > 0 ? `${product.five_plus_one.toFixed(2)} €` : '-'}</td>
                    <td className="py-3 px-2 text-right">{product.ap > 0 ? `${product.ap.toFixed(2)} €` : '-'}</td>
                    <td className="py-3 px-2 text-right">{product.punkte > 0 ? product.punkte.toFixed(2) : '-'}</td>
                    <td className="py-3 px-2 text-right">{product.ust_percent}%</td>
                    <td className="py-3 px-2 text-right">
                      <div className="flex gap-1 justify-end">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleEdit(product)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="destructive"
                          onClick={() => handleDelete(product.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {products.length === 0 && (
              <div className="text-center py-12 text-slate-500">
                Noch keine Produkte vorhanden
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Preisliste Hinweis Modal */}
      {showHinweisModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-8">
            <div className="text-center">
              <div className="text-5xl mb-4">⚠️</div>
              <h2 className="text-2xl font-bold text-slate-800 mb-4">
                Hinweis zur Preisliste
              </h2>
              <div className="text-left text-slate-700 space-y-4 mb-6">
                <p>
                  Die hier dargestellten Preise und Berechnungen dienen ausschließlich der privaten Organisation von PM-International Teampartnern. Die Werte wurden selbst berechnet und können von offiziellen Angaben abweichen.
                </p>
                <p>
                  Es handelt sich nicht um offizielle Preislisten, Aktionen oder Dokumente von PM-International. Alle Angaben erfolgen ohne Gewähr.
                </p>
                <p className="font-semibold text-amber-800">
                  Bitte prüfen Sie alle Werte eigenverantwortlich.
                </p>
              </div>
              <button
                onClick={handleHinweisVerstanden}
                className="w-full px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold transition-colors text-lg"
              >
                Verstanden
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}